import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, ChevronRight, ChevronLeft, Loader2, Dumbbell,
  TrendingDown, TrendingUp, Heart, Activity, Zap, Mountain, Trophy,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { invalidateOnboardingCache } from "@/App";
import { NovaLogo } from "@/components/NovaLogo";

// 3-step onboarding flow (preceded by a welcome card):
//   Welcome  → context + reassurance
//   Step 1   → First name, age, gender, height, weight   (required)
//   Step 2   → Main goal                                   (required)
//   Step 3   → Activity level                              (required)
//
// On completion: PUT /profile, bust onboarding-status cache, navigate /home.

const GENDERS = ["male", "female", "non-binary", "prefer-not-to-say"];
const HEIGHT_PRESETS = ["5'2\"", "5'4\"", "5'6\"", "5'8\"", "5'10\"", "6'0\"", "6'2\""];
const WEIGHT_PRESETS = ["120 lbs", "140 lbs", "160 lbs", "180 lbs", "200 lbs", "220 lbs"];

const MAIN_GOALS = [
  { value: "gain-muscle",     label: "Gain Muscle",            sub: "Build lean mass with a small surplus", Icon: TrendingUp },
  { value: "lose-weight",     label: "Lose Weight",            sub: "Sustainable calorie deficit",          Icon: TrendingDown },
  { value: "maintain-weight", label: "Maintain Weight",        sub: "Hold steady, dial in habits",          Icon: Activity },
  { value: "improve-health",  label: "Improve Overall Health", sub: "Energy, sleep, longevity",             Icon: Heart },
];

const ACTIVITY_LEVELS = [
  { value: "sedentary",         label: "Sedentary",         sub: "Mostly sitting, little exercise",                Icon: Zap },
  { value: "lightly-active",    label: "Lightly Active",    sub: "Light walks or workouts 1–3 days a week",         Icon: Activity },
  { value: "moderately-active", label: "Moderately Active", sub: "Moderate exercise 3–5 days a week",               Icon: Dumbbell },
  { value: "very-active",       label: "Very Active",       sub: "Hard exercise 6–7 days a week",                   Icon: Mountain },
  { value: "athlete",           label: "Athlete",           sub: "Twice-a-day training or physical job",            Icon: Trophy },
];

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="chip"
      style={{
        background: active ? "var(--gold)" : "transparent",
        color: active ? "#000" : "var(--text)",
        borderColor: active ? "var(--gold)" : "var(--line)",
        cursor: "pointer",
        fontWeight: active ? 700 : 500,
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, children, testId }) {
  return (
    <div style={{ marginBottom: 16 }} data-testid={testId}>
      <div
        className="eyebrow"
        style={{ marginBottom: 8, fontSize: 10, color: "var(--text-dim)" }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function GoalCard({ value, label, sub, Icon, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "16px 14px",
        marginBottom: 10,
        borderRadius: 14,
        cursor: "pointer",
        background: active ? "rgba(232,211,185,0.10)" : "var(--surface-soft)",
        border: active ? "1.5px solid var(--gold)" : "1px solid var(--line)",
        color: "var(--text)",
        display: "flex",
        alignItems: "center",
        gap: 14,
        transition: "background 120ms ease, border-color 120ms ease",
      }}
    >
      <div
        style={{
          width: 40, height: 40, flexShrink: 0,
          borderRadius: 10,
          background: active ? "var(--gold)" : "rgba(255,255,255,0.04)",
          color: active ? "#000" : "var(--gold)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Icon size={20} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>{label}</div>
        <div className="tiny" style={{ color: "var(--text-dim)", lineHeight: 1.4 }}>{sub}</div>
      </div>
      {active && <Sparkles size={16} color="var(--gold)" />}
      {/* hidden value attr so test selectors can read it */}
      <span hidden>{value}</span>
    </button>
  );
}

function StepDots({ step, total = 3 }) {
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 18 }} data-testid="onb-step-dots">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 4, flex: 1, borderRadius: 2,
            background: i < step ? "var(--gold)" : "rgba(255,255,255,0.10)",
            transition: "background 200ms ease",
          }}
        />
      ))}
    </div>
  );
}

export default function OnboardingScreen() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  // "welcome" → "basics" → "goal" → "activity"
  const [step, setStep] = useState("welcome");

  // Step 1 — Basics
  const [name, setName]     = useState(user?.name || "");
  const [age, setAge]       = useState("");
  const [gender, setGender] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");

  // Step 2 — Main goal
  const [mainGoal, setMainGoal] = useState("");

  // Step 3 — Activity level
  const [activity, setActivity] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  // If already onboarded, skip straight to home.
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const { data } = await api.get("/profile/onboarding-status");
        if (!aborted && data?.onboarded) navigate("/home", { replace: true });
      } catch (_) { /* allow user to onboard anyway */ }
    })();
    return () => { aborted = true; };
  }, [navigate]);

  useEffect(() => {
    if (user?.name && !name) setName(user.name);
  }, [user, name]);

  const basicsValid =
    name.trim().length >= 1 &&
    /^\d{1,3}$/.test(age) && Number(age) >= 10 && Number(age) <= 110 &&
    !!gender && !!height.trim() && !!weight.trim();

  const submit = async () => {
    if (saving || !basicsValid || !mainGoal || !activity) return;
    setError(""); setSaving(true);
    try {
      await api.put("/profile", {
        name: name.trim(),
        age: Number(age),
        gender,
        height: height.trim(),
        weight: weight.trim(),
        main_goal: mainGoal,
        activity_level: activity,
      });
      invalidateOnboardingCache();
      try { await refresh?.(); } catch (_e) { /* refresh best-effort */ }
      navigate("/home", { replace: true });
    } catch (e) {
      setError(
        e?.response?.data?.detail
          ? String(e.response.data.detail).slice(0, 240)
          : "Couldn't save. Please try again.",
      );
    } finally { setSaving(false); }
  };

  return (
    <div className="screen fade-in" data-testid="onboarding-screen">
      <div className="top-bar">
        <div className="logo-mark" data-testid="onboarding-logo" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <NovaLogo size={20} />
          <span>NOVA</span>
        </div>
        {/* No skip — every brand-new account must finish all 3 steps. */}
      </div>

      {/* WELCOME */}
      {step === "welcome" && (
        <div data-testid="onboarding-welcome">
          <div className="eyebrow" style={{ marginBottom: 16, color: "var(--gold)" }}>
            <Sparkles size={12} strokeWidth={1.8} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />
            WELCOME TO NOVA
          </div>
          <h1 className="h1" style={{ marginBottom: 16, fontSize: 32, lineHeight: 1.12 }}>
            Made for <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>you</em>,
            <br />
            from the first tap.
          </h1>
          <p
            className="body"
            style={{ color: "var(--text-dim)", lineHeight: 1.6, marginTop: 0, marginBottom: 22, fontSize: 15 }}
            data-testid="onb-welcome-message"
          >
            We use a few details to personalise your <strong style={{ color: "var(--text)" }}>workouts</strong>,{" "}
            <strong style={{ color: "var(--text)" }}>nutrition</strong>,{" "}
            <strong style={{ color: "var(--text)" }}>style</strong>,{" "}
            <strong style={{ color: "var(--text)" }}>skincare</strong>, and{" "}
            <strong style={{ color: "var(--text)" }}>AI recommendations</strong>.
            You can update this information anytime in your Profile.
          </p>

          <div
            className="card"
            data-testid="onb-welcome-card"
            style={{
              padding: 18, marginBottom: 22,
              border: "1px solid rgba(232,211,185,0.30)",
              background: "rgba(232,211,185,0.05)",
            }}
          >
            <div className="eyebrow" style={{ color: "var(--gold)", marginBottom: 12 }}>3 QUICK STEPS</div>
            <ul className="bullet-list" style={{ margin: 0, color: "var(--text)", lineHeight: 1.7 }}>
              <li>Your basics — so the AI knows your build</li>
              <li>Your main goal — so we calibrate the plan</li>
              <li>Your activity level — so calorie targets are right</li>
            </ul>
          </div>

          <button
            type="button"
            className="btn btn-gold"
            data-testid="onb-welcome-continue-btn"
            onClick={() => setStep("basics")}
            style={{ width: "100%", justifyContent: "center" }}
          >
            Let&apos;s go <ArrowRight size={16} />
          </button>
          <div className="tiny" style={{ marginTop: 14, textAlign: "center", color: "var(--text-faint)", lineHeight: 1.55 }}>
            Takes about 60 seconds. Edit anytime from Profile.
          </div>
        </div>
      )}

      {/* STEP 1 — BASICS */}
      {step === "basics" && (
        <div data-testid="onboarding-step-basics">
          <StepDots step={1} />
          <div className="eyebrow" style={{ marginBottom: 12 }}>STEP 1 OF 3 · BASICS</div>
          <h2 className="h2" style={{ fontSize: 24, marginBottom: 18, lineHeight: 1.18 }}>
            Tell us about you.
          </h2>

          <Field label="FIRST NAME" testId="onb-field-name">
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="First name" autoComplete="given-name"
              data-testid="onb-input-name" className="input"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 10, background: "var(--surface-soft)", color: "var(--text)", border: "1px solid var(--line)", fontSize: 16 }}
            />
          </Field>

          <Field label="AGE" testId="onb-field-age">
            <input
              type="number" value={age}
              onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
              placeholder="28" inputMode="numeric"
              data-testid="onb-input-age" className="input"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 10, background: "var(--surface-soft)", color: "var(--text)", border: "1px solid var(--line)", fontSize: 16 }}
            />
          </Field>

          <Field label="GENDER" testId="onb-field-gender">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {GENDERS.map((g) => (
                <Chip key={g} active={gender === g} onClick={() => setGender(g)} testId={`onb-gender-${g}`}>
                  {g.replace(/-/g, " ")}
                </Chip>
              ))}
            </div>
          </Field>

          <Field label="HEIGHT" testId="onb-field-height">
            <input
              type="text" value={height} onChange={(e) => setHeight(e.target.value)}
              placeholder={"e.g. 5'10\" or 178 cm"}
              data-testid="onb-input-height" className="input"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 10, background: "var(--surface-soft)", color: "var(--text)", border: "1px solid var(--line)", fontSize: 16, marginBottom: 8 }}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {HEIGHT_PRESETS.map((h) => (
                <Chip key={h} active={height === h} onClick={() => setHeight(h)} testId={`onb-height-${h.replace(/[^0-9a-z]/gi, "")}`}>{h}</Chip>
              ))}
            </div>
          </Field>

          <Field label="WEIGHT" testId="onb-field-weight">
            <input
              type="text" value={weight} onChange={(e) => setWeight(e.target.value)}
              placeholder="e.g. 165 lbs or 75 kg"
              data-testid="onb-input-weight" className="input"
              style={{ width: "100%", padding: "12px 14px", borderRadius: 10, background: "var(--surface-soft)", color: "var(--text)", border: "1px solid var(--line)", fontSize: 16, marginBottom: 8 }}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {WEIGHT_PRESETS.map((w) => (
                <Chip key={w} active={weight === w} onClick={() => setWeight(w)} testId={`onb-weight-${w.replace(/[^0-9a-z]/gi, "")}`}>{w}</Chip>
              ))}
            </div>
          </Field>

          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button
              type="button" className="btn btn-outline"
              data-testid="onb-basics-back"
              onClick={() => setStep("welcome")}
              style={{ minWidth: 96, justifyContent: "center" }}
            >
              <ChevronLeft size={16} /> Back
            </button>
            <button
              type="button" className="btn btn-gold" disabled={!basicsValid}
              data-testid="onb-basics-next"
              onClick={() => setStep("goal")}
              style={{ flex: 1, justifyContent: "center", opacity: basicsValid ? 1 : 0.55, cursor: basicsValid ? "pointer" : "not-allowed" }}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 2 — MAIN GOAL */}
      {step === "goal" && (
        <div data-testid="onboarding-step-goal">
          <StepDots step={2} />
          <div className="eyebrow" style={{ marginBottom: 12 }}>STEP 2 OF 3 · YOUR GOAL</div>
          <h2 className="h2" style={{ fontSize: 24, marginBottom: 16, lineHeight: 1.18 }}>
            What&apos;s your main goal?
          </h2>
          <p className="body-sm" style={{ color: "var(--text-dim)", marginBottom: 18 }}>
            We calibrate calories, macros, and workouts around this.
          </p>

          {MAIN_GOALS.map((g) => (
            <GoalCard
              key={g.value}
              {...g}
              active={mainGoal === g.value}
              onClick={() => setMainGoal(g.value)}
              testId={`onb-main-goal-${g.value}`}
            />
          ))}

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button
              type="button" className="btn btn-outline"
              data-testid="onb-goal-back"
              onClick={() => setStep("basics")}
              style={{ minWidth: 96, justifyContent: "center" }}
            >
              <ChevronLeft size={16} /> Back
            </button>
            <button
              type="button" className="btn btn-gold" disabled={!mainGoal}
              data-testid="onb-goal-next"
              onClick={() => setStep("activity")}
              style={{ flex: 1, justifyContent: "center", opacity: mainGoal ? 1 : 0.55, cursor: mainGoal ? "pointer" : "not-allowed" }}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — ACTIVITY LEVEL */}
      {step === "activity" && (
        <div data-testid="onboarding-step-activity">
          <StepDots step={3} />
          <div className="eyebrow" style={{ marginBottom: 12 }}>STEP 3 OF 3 · ACTIVITY</div>
          <h2 className="h2" style={{ fontSize: 24, marginBottom: 16, lineHeight: 1.18 }}>
            How active are you?
          </h2>
          <p className="body-sm" style={{ color: "var(--text-dim)", marginBottom: 18 }}>
            This decides your calorie target — pick the option closest to your week.
          </p>

          {ACTIVITY_LEVELS.map((a) => (
            <GoalCard
              key={a.value}
              {...a}
              active={activity === a.value}
              onClick={() => setActivity(a.value)}
              testId={`onb-activity-${a.value}`}
            />
          ))}

          {error && (
            <div
              className="card" data-testid="onb-error"
              style={{
                padding: 12, marginTop: 14, marginBottom: 4,
                border: "1px solid rgba(255,120,120,0.30)",
                background: "rgba(255,120,120,0.06)",
                color: "#ffb4b4", fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button
              type="button" className="btn btn-outline"
              data-testid="onb-activity-back"
              onClick={() => setStep("goal")}
              style={{ minWidth: 96, justifyContent: "center" }}
            >
              <ChevronLeft size={16} /> Back
            </button>
            <button
              type="button" className="btn btn-gold"
              disabled={!activity || saving}
              data-testid="onb-finish-btn"
              onClick={submit}
              style={{ flex: 1, justifyContent: "center", opacity: (!activity || saving) ? 0.55 : 1, cursor: (!activity || saving) ? "not-allowed" : "pointer" }}
            >
              {saving
                ? (<><Loader2 size={16} className="spin" /> Saving…</>)
                : (<>Enter app <ChevronRight size={16} /></>)}
            </button>
          </div>

          <div className="tiny" style={{ marginTop: 16, textAlign: "center", color: "var(--text-faint)", lineHeight: 1.55 }}>
            You can change everything anytime from Profile.
          </div>
        </div>
      )}
    </div>
  );
}
